/**
 * Storage Utilities
 *
 * Centralized storage helpers for sessionStorage and localStorage.
 * SECURITY: Tokens and sensitive data should ONLY use sessionStorage.
 */

/**
 * Storage keys used throughout the application
 */
export const STORAGE_KEYS = {
    // Session-only (cleared on browser close) - for sensitive data
    ENTRA_ORG_ID: 'entraOrgId',
    CONVERSATION_ID: 'conversationId',
    GENESYS_SESSION_TOKEN: 'genesysSessionToken',
    BUSINESS_REQUEST_ID: 'businessRequestId',
    CHAT_SESSION_ID: 'chatSessionId',
    CHAT_OWNER_TYPE: 'chatOwnerType',
    // Server-issued ResourceAPI selectors (v4). Kept strictly distinct from the
    // client-generated CONVERSATION_ID UUID — assist_conversation_id is a server id.
    RESOURCEAPI_PLANNING_APPLICATION_ID: 'resourceApiPlanningApplicationId',
    RESOURCEAPI_ASSIST_CONVERSATION_ID: 'resourceApiAssistConversationId',

    // Session storage for user context
    ADDRESS: 'address',
    PROP_ID: 'propId',
    GURAS_ID: 'gurasId',
    COUNCIL_NAME: 'CouncilName',
    COUNCIL_WEBSITE_URL: 'CouncilWebsiteURL',
    ZONE: 'zone',
    ZONE_DESCRIPTION: 'zoneDescription',
    LOT_SIZE: 'lotSize',
    LOT_WIDTH: 'lotWidth',
    FLOOR_SPACE_RATIO: 'floorSpaceRatio',
    MINE_SUBSIDENCE_AFFECTS: 'mineSubsidenceAffects',
    MIN_LOT_SIZE: 'minLotSize',
    MAX_BUILDING_HEIGHT: 'maxBuildingHeight',
    ACTIVE_PRECINCT: 'activePrecinct',
    PROPERTY_PRECINCT: 'propertyPrecinct',
    IS_BATTLE_AXE_LOT: 'isBattleAxeLot',
    IS_CORNER_LOT: 'isCornerLot',

    // localStorage (persists) - for non-sensitive data
    CHAT_HISTORY: 'chatHistory',
    CHAT_CONTEXT: 'daisy-chat-context',
    CHAT_BUBBLE_CLOSED: 'chatBubbleClosed',
    USER_PREFERENCES: 'userPreferences',
    SUGGESTIONS: 'chatSuggestions',
    LAST_PROJECT_CONTEXT: 'eai-last-project-context'
};

export const CHAT_STORAGE_VERSION = 1;

const LEGACY_CONVERSATION_ID_KEYS = ['threadId', 'thread_id', 'daisy_thread_id'];

export function migrateConversationIdStorage() {
    const currentConversationId = sessionStore.get(STORAGE_KEYS.CONVERSATION_ID);

    if (currentConversationId) {
        LEGACY_CONVERSATION_ID_KEYS.forEach((key) => sessionStore.remove(key));
        return currentConversationId;
    }

    const legacyConversationId = LEGACY_CONVERSATION_ID_KEYS.map((key) => sessionStore.get(key)).find(
        (value) => typeof value === 'string' && value.trim()
    );

    LEGACY_CONVERSATION_ID_KEYS.forEach((key) => sessionStore.remove(key));

    if (!legacyConversationId) {
        return null;
    }

    const conversationId = legacyConversationId.trim();
    sessionStore.set(STORAGE_KEYS.CONVERSATION_ID, conversationId);
    return conversationId;
}

/**
 * Read the last structured project continuation pointer from localStorage.
 * This is intentionally non-sensitive and cannot authorize access by itself.
 *
 * @returns {{ businessRequestId: string, tenantId: string, ownerType: 'anonymous'|'user', source: 'widget'|'portal', updatedAt: string } | null}
 */
export function getLastProjectContext() {
    const parsed = localStore.getJSON(STORAGE_KEYS.LAST_PROJECT_CONTEXT);
    if (!parsed || typeof parsed !== 'object') {
        return null;
    }

    if (
        typeof parsed.businessRequestId !== 'string' ||
        typeof parsed.tenantId !== 'string' ||
        (parsed.ownerType !== 'anonymous' && parsed.ownerType !== 'user') ||
        (parsed.source !== 'widget' && parsed.source !== 'portal') ||
        typeof parsed.updatedAt !== 'string'
    ) {
        return null;
    }

    // v4 ResourceAPI selectors are OPTIONAL: validated only if present. A pre-migration
    // context (no new fields) still validates and restores unchanged (FE-AC-029) —
    // requiring them here would wipe every existing user's continuation pointer. A
    // present-but-wrong-typed field is nulled without invalidating the rest.
    const context = { ...parsed };
    for (const key of [
        STORAGE_KEYS.RESOURCEAPI_PLANNING_APPLICATION_ID,
        STORAGE_KEYS.RESOURCEAPI_ASSIST_CONVERSATION_ID
    ]) {
        if (key in context && typeof context[key] !== 'string') {
            context[key] = null;
        }
    }

    return context;
}

/**
 * Persist the last structured project continuation pointer in localStorage.
 *
 * @param {{ businessRequestId: string, tenantId: string, ownerType: 'anonymous'|'user', source: 'widget'|'portal', updatedAt?: string }} context
 */
export function setLastProjectContext(context) {
    if (!context?.businessRequestId || !context?.tenantId) {
        return;
    }

    const payload = {
        ...context,
        updatedAt: context.updatedAt || new Date().toISOString()
    };

    // Persist the optional ResourceAPI selectors only when they are real strings.
    // null/undefined -> omitted, so the stored shape stays byte-identical to the
    // legacy pointer for flags-off traffic (FE-AC-020 / FE-AC-029).
    for (const key of [
        STORAGE_KEYS.RESOURCEAPI_PLANNING_APPLICATION_ID,
        STORAGE_KEYS.RESOURCEAPI_ASSIST_CONVERSATION_ID
    ]) {
        if (typeof payload[key] !== 'string') {
            delete payload[key];
        }
    }

    localStore.setJSON(STORAGE_KEYS.LAST_PROJECT_CONTEXT, payload);
}

/**
 * Remove the last structured project continuation pointer from localStorage.
 */
export function clearLastProjectContext() {
    localStore.remove(STORAGE_KEYS.LAST_PROJECT_CONTEXT);
}

/**
 * Session Storage wrapper - for sensitive data
 * Data is cleared when browser session ends
 */
export const sessionStore = {
    /**
     * Get a value from sessionStorage
     * @param {string} key - Storage key
     * @returns {string|null} - Stored value or null
     */
    get(key) {
        try {
            return sessionStorage.getItem(key);
        } catch (e) {
            console.error('SessionStorage get error:', e);
            return null;
        }
    },

    /**
     * Set a value in sessionStorage
     * @param {string} key - Storage key
     * @param {string} value - Value to store
     */
    set(key, value) {
        try {
            sessionStorage.setItem(key, value);
        } catch (e) {
            console.error('SessionStorage set error:', e);
        }
    },

    /**
     * Get a JSON-parsed value from sessionStorage
     * @param {string} key - Storage key
     * @returns {*} - Parsed value or null
     */
    getJSON(key) {
        try {
            const value = sessionStorage.getItem(key);
            return value ? JSON.parse(value) : null;
        } catch (e) {
            console.error('SessionStorage getJSON error:', e);
            return null;
        }
    },

    /**
     * Set a JSON-stringified value in sessionStorage
     * @param {string} key - Storage key
     * @param {*} value - Value to store (will be JSON.stringify'd)
     */
    setJSON(key, value) {
        try {
            sessionStorage.setItem(key, JSON.stringify(value));
        } catch (e) {
            console.error('SessionStorage setJSON error:', e);
        }
    },

    /**
     * Remove a value from sessionStorage
     * @param {string} key - Storage key
     */
    remove(key) {
        try {
            sessionStorage.removeItem(key);
        } catch (e) {
            console.error('SessionStorage remove error:', e);
        }
    },

    /**
     * Clear all sessionStorage
     */
    clear() {
        try {
            sessionStorage.clear();
        } catch (e) {
            console.error('SessionStorage clear error:', e);
        }
    }
};

/**
 * Local Storage wrapper - for non-sensitive, persistent data
 * Data persists across browser sessions
 */
export const localStore = {
    /**
     * Get a value from localStorage
     * @param {string} key - Storage key
     * @returns {string|null} - Stored value or null
     */
    get(key) {
        try {
            return localStorage.getItem(key);
        } catch (e) {
            console.error('LocalStorage get error:', e);
            return null;
        }
    },

    /**
     * Set a value in localStorage
     * @param {string} key - Storage key
     * @param {string} value - Value to store
     */
    set(key, value) {
        try {
            localStorage.setItem(key, value);
        } catch (e) {
            console.error('LocalStorage set error:', e);
        }
    },

    /**
     * Get a JSON-parsed value from localStorage
     * @param {string} key - Storage key
     * @returns {*} - Parsed value or null
     */
    getJSON(key) {
        try {
            const value = localStorage.getItem(key);
            return value ? JSON.parse(value) : null;
        } catch (e) {
            console.error('LocalStorage getJSON error:', e);
            return null;
        }
    },

    /**
     * Set a JSON-stringified value in localStorage
     * @param {string} key - Storage key
     * @param {*} value - Value to store (will be JSON.stringify'd)
     */
    setJSON(key, value) {
        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch (e) {
            console.error('LocalStorage setJSON error:', e);
        }
    },

    /**
     * Remove a value from localStorage
     * @param {string} key - Storage key
     */
    remove(key) {
        try {
            localStorage.removeItem(key);
        } catch (e) {
            console.error('LocalStorage remove error:', e);
        }
    },

    /**
     * Clear all localStorage
     */
    clear() {
        try {
            localStorage.clear();
        } catch (e) {
            console.error('LocalStorage clear error:', e);
        }
    }
};

export function normalizeChatStorageContext(context) {
    if (!context || typeof context !== 'object') {
        return null;
    }

    const tenantId = typeof context.tenantId === 'string' ? context.tenantId.trim() : '';
    const workflowId = typeof context.workflowId === 'string' ? context.workflowId.trim() : '';
    const councilName = typeof context.councilName === 'string' ? context.councilName.trim() : '';

    if (!tenantId && !workflowId && !councilName) {
        return null;
    }

    return {
        tenantId,
        workflowId,
        councilName
    };
}

export function areChatStorageContextsEqual(left, right) {
    const normalizedLeft = normalizeChatStorageContext(left);
    const normalizedRight = normalizeChatStorageContext(right);

    if (!normalizedLeft || !normalizedRight) {
        return false;
    }

    return (
        normalizedLeft.tenantId === normalizedRight.tenantId &&
        normalizedLeft.workflowId === normalizedRight.workflowId &&
        normalizedLeft.councilName.toLowerCase() === normalizedRight.councilName.toLowerCase()
    );
}

function isContextualStorageEnvelope(value) {
    return (
        value &&
        typeof value === 'object' &&
        value.version === CHAT_STORAGE_VERSION &&
        normalizeChatStorageContext(value.context)
    );
}

export function clearTenantBoundStorage() {
    [
        STORAGE_KEYS.CONVERSATION_ID,
        STORAGE_KEYS.GENESYS_SESSION_TOKEN,
        STORAGE_KEYS.BUSINESS_REQUEST_ID,
        STORAGE_KEYS.CHAT_SESSION_ID,
        STORAGE_KEYS.CHAT_OWNER_TYPE,
        STORAGE_KEYS.RESOURCEAPI_PLANNING_APPLICATION_ID,
        STORAGE_KEYS.RESOURCEAPI_ASSIST_CONVERSATION_ID,
        STORAGE_KEYS.ADDRESS,
        STORAGE_KEYS.PROP_ID,
        STORAGE_KEYS.GURAS_ID,
        STORAGE_KEYS.COUNCIL_NAME,
        STORAGE_KEYS.COUNCIL_WEBSITE_URL,
        STORAGE_KEYS.ZONE,
        STORAGE_KEYS.ZONE_DESCRIPTION,
        STORAGE_KEYS.LOT_SIZE,
        STORAGE_KEYS.LOT_WIDTH,
        STORAGE_KEYS.MIN_LOT_SIZE,
        STORAGE_KEYS.MAX_BUILDING_HEIGHT,
        STORAGE_KEYS.ACTIVE_PRECINCT,
        STORAGE_KEYS.IS_BATTLE_AXE_LOT,
        STORAGE_KEYS.IS_CORNER_LOT,
        'eaiAnonAssetSession',
        'invalidAddressCards',
        'currentProject',
        'projects',
        'tenantId',
        'workflowId',
        'threadId',
        'thread_id',
        'daisy_thread_id',
        'projectDataObject',
        'policiesDataObject',
        'Heritage_Item_ID',
        'Heritage_Property_More_Info_URL',
        'propertyReport'
    ].forEach((key) => sessionStore.remove(key));

    [STORAGE_KEYS.CHAT_HISTORY, STORAGE_KEYS.SUGGESTIONS, STORAGE_KEYS.LAST_PROJECT_CONTEXT].forEach((key) =>
        localStore.remove(key)
    );
}

export function ensureChatStorageContext(context) {
    const normalizedContext = normalizeChatStorageContext(context);
    if (!normalizedContext) {
        return {
            context: null,
            changed: false
        };
    }

    const storedContextEnvelope = localStore.getJSON(STORAGE_KEYS.CHAT_CONTEXT);
    const storedChatHistory = localStore.getJSON(STORAGE_KEYS.CHAT_HISTORY);
    const hasLegacyChatHistory = Array.isArray(storedChatHistory);
    const hasWrongHistoryEnvelope =
        storedChatHistory &&
        !Array.isArray(storedChatHistory) &&
        (!isContextualStorageEnvelope(storedChatHistory) ||
            !areChatStorageContextsEqual(storedChatHistory.context, normalizedContext));
    const hasWrongContextEnvelope =
        storedContextEnvelope &&
        (!isContextualStorageEnvelope(storedContextEnvelope) ||
            !areChatStorageContextsEqual(storedContextEnvelope.context, normalizedContext));
    const hasUnscopedTenantSessionState =
        !storedContextEnvelope &&
        [
            STORAGE_KEYS.BUSINESS_REQUEST_ID,
            STORAGE_KEYS.CHAT_SESSION_ID,
            STORAGE_KEYS.CHAT_OWNER_TYPE,
            STORAGE_KEYS.ADDRESS,
            STORAGE_KEYS.PROP_ID,
            'eaiAnonAssetSession',
            'currentProject',
            'projectDataObject',
            'policiesDataObject'
        ].some((key) => sessionStore.get(key));

    const changed = Boolean(
        hasLegacyChatHistory || hasWrongHistoryEnvelope || hasWrongContextEnvelope || hasUnscopedTenantSessionState
    );

    if (changed) {
        clearTenantBoundStorage();
    }

    localStore.setJSON(STORAGE_KEYS.CHAT_CONTEXT, {
        version: CHAT_STORAGE_VERSION,
        context: normalizedContext
    });

    return {
        context: normalizedContext,
        changed
    };
}

/**
 * Generate a unique ID for messages, sessions, etc.
 * @returns {string} - UUID v4 format string
 */
export function generateUUID() {
    const cryptoSource = globalThis.crypto;

    if (cryptoSource?.randomUUID) {
        return cryptoSource.randomUUID();
    }

    if (!cryptoSource?.getRandomValues) {
        throw new Error('Web Crypto API is required to generate chat identifiers');
    }

    const bytes = new Uint8Array(16);
    cryptoSource.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));
    return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
}
